import React, { forwardRef } from 'react';
import { formatPaise } from '../lib/cn';
import { cn } from '../lib/cn';
import { CheckCircle2 } from 'lucide-react';

export interface VehicleUtilizationBarProps extends React.HTMLAttributes<HTMLDivElement> {
  totalPaise: number;
  bagCount: number;
  weightKg?: number;
  vehicleCapacityKg?: number;
  minOrderPaise?: number;
}

export const VehicleUtilizationBar = forwardRef<HTMLDivElement, VehicleUtilizationBarProps>(
  ({ totalPaise, bagCount, weightKg = 0, vehicleCapacityKg = 1000, minOrderPaise, className, ...props }, ref) => {
    
    // Utilization based on weight if provided and capacity is greater than 0
    let utilization = 0;
    if (vehicleCapacityKg > 0) {
      utilization = Math.min(100, Math.max(0, (weightKg / vehicleCapacityKg) * 100));
    }
    
    let colorClass = "bg-success";
    if (utilization >= 90) {
      colorClass = "bg-destructive";
    } else if (utilization >= 70) {
      colorClass = "bg-amber-500";
    }

    const minOrderMet = minOrderPaise !== undefined && totalPaise >= minOrderPaise;

    return (
      <div ref={ref} className={cn("space-y-3 w-full", className)} {...props}>
        <div className="flex justify-between items-center text-sm">
          <div className="flex gap-4">
            <div>
              <span className="text-muted-foreground text-xs block">Order Total</span>
              <span className="font-bold">{formatPaise(totalPaise)}</span>
            </div>
            <div>
              <span className="text-muted-foreground text-xs block">Bags</span>
              <span className="font-semibold">{bagCount}</span>
            </div>
          </div>
          
          {minOrderPaise !== undefined && (
            <div className="text-right flex items-center gap-1.5">
              <div className="flex flex-col items-end">
                <span className="text-muted-foreground text-xs">Min. Order</span>
                <span className={cn("font-medium text-xs", minOrderMet ? "text-success" : "text-muted-foreground")}>
                  {formatPaise(minOrderPaise)}
                </span>
              </div>
              {minOrderMet && <CheckCircle2 className="w-5 h-5 text-success" />}
            </div>
          )}
        </div>
        
        {vehicleCapacityKg > 0 && weightKg > 0 && (
          <div className="space-y-1.5 mt-2">
            <div className="flex justify-between text-xs text-muted-foreground">
              <span>Vehicle Fill: {Math.round(utilization)}%</span>
              <span>{weightKg} / {vehicleCapacityKg} kg</span>
            </div>
            <div className="h-2 w-full bg-secondary rounded-full overflow-hidden">
              <div 
                className={cn("h-full transition-all duration-500", colorClass)} 
                style={{ width: `${utilization}%` }}
                role="progressbar"
                aria-valuenow={utilization}
                aria-valuemin={0}
                aria-valuemax={100}
              />
            </div>
          </div>
        )}
      </div>
    );
  }
);

VehicleUtilizationBar.displayName = 'VehicleUtilizationBar';
